import { useEffect, useState } from 'react'
import { Link, Navigate, useParams } from 'react-router-dom'
import { ArrowUpRightIcon } from '../components/ArrowUpRightIcon'
import { Breadcrumbs } from '../components/Breadcrumbs'
import { FaqList } from '../components/FaqList'
import { refreshStations, useStations } from '../data/catalog'
import { getStation } from '../data/stations'
import { LINKS } from '../config/links'
import { Seo } from '../seo/Seo'
import { breadcrumbSchema, faqSchema, organizationSchema, stationSchema } from '../seo/schema'
import { absoluteUrl } from '../seo/site'
import { topicsForStation } from '../seo/topics'
import '../seo/copy.css'
import './StationDetail.css'

export function StationDetail() {
  const { slug = '' } = useParams()
  const stations = useStations()
  const [settled, setSettled] = useState(false)
  useEffect(() => {
    void refreshStations().finally(() => setSettled(true))
  }, [])
  const station = getStation(slug) ?? stations.find((item) => item.slug === slug)

  if (!station) {
    if (!settled) return null
    return <Navigate to="/stations" replace />
  }

  const listenUrl = `${LINKS.webApp}/?station=${encodeURIComponent(station.streamId)}`
  const related = stations.filter((item) => item.slug !== station.slug).slice(0, 5)
  const guides = topicsForStation(station)
  const faqs = [
    {
      q: `How do I listen to ${station.name}?`,
      a: `Open ${station.name} on Music24 in the browser or the app and press play. It is a free live stream, with no account.`,
    },
    {
      q: `Is the ${station.name} station free?`,
      a: `Yes. ${station.name} is part of Music24 Jewish music radio. There is no subscription and no ads in the stream.`,
    },
    {
      q: `Is ${station.name} still on Music24?`,
      a: `${station.name} is in the current Music24 catalog, so this page stays up while the station is on air. Hidden stations are removed from the list.`,
    },
  ]

  return (
    <main className="station-page shell">
      <Seo
        title={`${station.name} Jewish Music Radio`}
        description={station.description}
        path={`/stations/${station.slug}`}
        keywords={[
          `${station.name} radio`,
          `${station.name} jewish music`,
          'jewish music radio',
          'jewish radio',
          ...station.keywords,
        ]}
        image={absoluteUrl(station.icon)}
        type="music.radio_station"
        jsonLd={[
          organizationSchema(),
          stationSchema(station),
          faqSchema(faqs),
          breadcrumbSchema([
            { name: 'Home', path: '/' },
            { name: 'Stations', path: '/stations' },
            { name: station.name, path: `/stations/${station.slug}` },
          ]),
        ]}
      />

      <Breadcrumbs
        items={[
          { name: 'Home', path: '/' },
          { name: 'Stations', path: '/stations' },
          { name: station.name },
        ]}
      />

      <article>
        <header className="station-page__hero">
          <img
            className="station-page__art"
            src={station.icon}
            alt={`${station.name} Jewish music radio station`}
            width={140}
            height={140}
          />

          <div className="station-page__intro">
            <h1>
              {station.name}
              <span>Jewish music radio</span>
            </h1>
            <div className="station-page__actions">
              <a
                className="btn btn--primary"
                href={listenUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                Listen now <ArrowUpRightIcon />
              </a>
              <Link className="btn btn--ghost" to="/download">
                Get the app
              </Link>
            </div>
          </div>
        </header>

        <section className="station-page__copy seo-prose">
          <p>{station.description}</p>
          <p>
            {station.name} is one station on Music24, a free Jewish music stream that runs 24/6.
            The station list comes from the live catalog, so this page is here while {station.name} is
            on air. Start it in the web player, or keep Music24 on your phone.
          </p>
          {guides.length > 0 && (
            <p>
              More to listen to:{' '}
              {guides.map((topic, index) => (
                <span key={topic.path}>
                  {index > 0 && ', '}
                  <Link to={topic.path}>{topic.nav}</Link>
                </span>
              ))}
              .
            </p>
          )}
        </section>

        <section className="station-page__faq" aria-labelledby="station-faq">
          <h2 id="station-faq">Questions</h2>
          <FaqList items={faqs} />
        </section>

        <section className="station-page__related" aria-labelledby="more-stations">
          <div className="station-page__related-head">
            <h2 id="more-stations">More stations</h2>
            <Link to="/stations">View all</Link>
          </div>
          <ul>
            {related.map((item) => (
              <li key={item.slug}>
                <Link to={`/stations/${item.slug}`}>
                  <img src={item.icon} alt="" width={48} height={48} />
                  <span>{item.name}</span>
                </Link>
              </li>
            ))}
          </ul>
        </section>
      </article>
    </main>
  )
}
